/* eslint-disable camelcase */
import { fromJS, Map } from 'immutable'
import * as Yup from 'yup'
import request from 'services/request'
import get from 'lodash/get'
import set from 'lodash/set'
import isNull from 'lodash/isNull'
import pick from 'lodash/pick'
import { USER_LANGUAGE_EN } from 'services/language'
import { USER_STATUS_ACTIVE, USER_STATUS_BLOCKED } from './status'
import { createAuthorization } from '../auth'

/**
 * The user detail store key
 */
export const USER_DETAIL_STORE_KEY = 'userDetail'

export const USER_ANONYMIZE_STORE_KEY = 'userAnonymize'
export const USER_GRANT_BONUS_STORE_KEY = 'userGrantBonus'

export const USER_DETAIL_INITIAL_VALUES = {
  username: '',
  email: '',
  password: '',
  passwordConfirm: '',
  status: USER_STATUS_ACTIVE,
  gcsi_user_uuid: null,
  first_name: '',
  last_name: '',
  billing_account_id: '',
  billing_account_number: '',
  user_roles: [],
  user_entitlements: [],
  language: USER_LANGUAGE_EN,
  uuid: '',
  member_support_notes: '',
}

const passwordRegex = /^(?=.*[A-Z])(?=.*[0-9])/
const gcsiUuidRegex = /^.{8}-.{4}-.{4}-.{4}-.{12}$/
export const USER_DETAIL_VALIDATION_SCHEMA = Yup.object().shape(
  {
    first_name: Yup.string().required('Required'),
    last_name: Yup.string().nullable(),
    username: Yup.string()
      .max(60, 'The username is too long: it must be 60 characters or less')
      .trim('The username cannot begin or end with a space')
      .test('isUsernameValid', 'The username contains an illegal character', (value) => {
        const illegalRegex = /[^\u0080-\u00F7 a-z0-9@_.\\'-]/i
        const nonPrintableRegex =
          /[\u0080-\u00A0\u00AD\u2000-\u200F\u2028-\u202F\u205F-\u206F\uFEFF\uFF01-\uFF60\uFFF9-\uFFFD\u0000-\u001f]/u
        if (illegalRegex.test(value) || nonPrintableRegex.test(value)) {
          return false
        }
        return true
      })
      .required('Required'),
    email: Yup.string().email('Email is invalid').required('Required'),
    password: Yup.string().when('password', {
      is: (exists) => !!exists,
      then: Yup.string()
        .min(7, 'Password must be at least 7 characters')
        .max(32, 'Passwords must contain no more than 32 characters')
        .matches(passwordRegex, 'Passwords must contain 1 uppercase letter and 1 number')
        .required('Required'),
      otherwise: Yup.string(),
    }),
    passwordConfirm: Yup.string().when('password', {
      is: (value) => !!(value && value.length > 0),
      then: Yup.string().oneOf([Yup.ref('password')], 'Both password need to be the same'),
    }),
    gcsi_user_uuid: Yup.string().nullable().matches(gcsiUuidRegex, 'GCSI UUID is invalid'),
  },
  [['password', 'password']],
)

export const getInitialValues = (data) => ({
  ...USER_DETAIL_INITIAL_VALUES,
  ...data,
  // checkboxes work with string value
  user_roles: data.user_roles.map(({ role }) => `${role}`),
  // radio works with string value
  language: data.language ? data.language[0] : USER_LANGUAGE_EN,
  // exclude unnecessary data
  user_entitlements: data.user_entitlements.map(
    ({ id, content_set_id, start, end, ecommerce_sku, ecommerce_sku_provider }) => ({
      id,
      content_set_id,
      start,
      end,
      ecommerce_sku,
      ecommerce_sku_provider,
    }),
  ),
})

export const preformValue = (values) => {
  const fields = [
    'username',
    'email',
    'password',
    'status',
    'gcsi_user_uuid',
    'user_roles',
    'user_entitlements',
    'uuid',
    'language',
    'first_name',
    'last_name',
    'billing_account_id',
    'billing_account_number',
    'member_support_notes',
  ]
  const data = pick(values, fields)

  // BE side works with numeric values
  data.user_roles = values.user_roles.map((role) => ({ role: +role }))
  // BE side works with array
  data.language = [values.language]
  // BE side works with numeric array
  data.user_entitlements = values.user_entitlements.map(({ content_set_id, ...rest }) => ({
    ...rest,
    content_set_id: parseInt(content_set_id, 10),
  }))

  return data
}

export async function simulateUser ({ server, cookie, auth, uid }) {
  const res = await request
    .post(`${server}/v1/user/simulate`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send({ uid, cookie })
  return fromJS(get(res, 'body', null))
}

/**
 * Search users by query
 */
export async function searchUsersByQuery ({
  auth,
  server,
  uid,
  uuid,
  billingAccountId,
  billingAccountNumber,
  username,
  usernameLike,
  email,
  emailLike,
  firstName,
  firstNameLike,
  lastName,
  lastNameLike,
  name,
  invoiceNumber,
  paymentNumber,
  ccLast4,
  recurringPaymentId,
  yogaInternationalUserId,
  page = 0,
  perPage = 20,
}) {
  const res = await request
    .post(`${server}/v2/user/search`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send({
      uid,
      uuid,
      billingAccountId,
      billingAccountNumber,
      username,
      usernameLike,
      email,
      emailLike,
      firstName,
      firstNameLike,
      lastName,
      lastNameLike,
      name,
      invoiceNumber,
      paymentNumber,
      ccLast4,
      recurringPaymentId,
      yogaInternationalUserId,
      page,
      perPage,
    })
  return get(res, 'body', [])
}

/*
 * Unblock a user
 */
export async function unblockUser ({ auth, server, uuid }) {
  const res = await request
    .post(`${server}/v1/user/reset-block`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send({ uuid })
  return get(res, 'body', {})
}

/**
 * Get user by uuid
 */
export async function getUserByUuid ({ auth, server, uuid }) {
  const res = await request
    .get(`${server}/v1/user/${uuid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', null))
}

/**
 * Get user by uid
 */
export async function getUserByUid ({ auth, server, uid }) {
  const res = await request
    .get(`${server}/v1/user/uid/${uid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', null))
}

export async function getProfiles ({ server, auth, user_account_id }) {
  const res = await request
    .get(`${server}/v1/user-account-admin/accountId/${user_account_id}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', null))
}

/**
 * Get user by gcsi_user_uuid
 */
export async function getUserByGcsiUserUuid ({ auth, server, gcsiUserUuid }) {
  const res = await request
    .get(`${server}/v1/user/gcsi/${gcsiUserUuid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  const body = get(res, 'body', null)
  return fromJS(body)
}

/**
 * Get user by uuid
 */
export async function getUserList ({ auth, server, uid }) {
  const res = await request
    .get(`${server}/v1/user/list`)
    // Converting into a Set makes a unique list of uid
    // then turn it back into a List to join
    .query({ uid: uid.toSet().toList().join(',') })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', null))
}

/*
 * Get the last transaction for the given user by uid
 */
export async function getUserLastTransaction ({ auth, server, uid }) {
  const res = await request
    .get(`${server}/last-transaction/${uid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', null))
}

/**
 * Get gcsi subscription details by gcsi_user_uuid
 */
export async function getUserGcsiSubscription ({ auth, server, gcsiUserUuid }) {
  const res = await request
    .get(`${server}/v1/user/gcsi/active-subscription/${gcsiUserUuid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', null))
}

/*
 * Create a user
 */
export async function createUser ({ auth, server, body }) {
  set(body, 'creation_source', 'admin')
  const res = await request
    .post(`${server}/v1/user`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return get(res, 'body', {})
}

/*
 * Update a user
 */
export async function updateUser ({ auth, server, body }) {
  const res = await request
    .put(`${server}/v1/user`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return get(res, 'body', {})
}

/*
 * Update a user's entitlements
 */
export async function updateUserEntitlement ({ auth, server, body }) {
  const res = await request
    .put(`${server}/v1/entitlement`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return get(res, 'body', {})
}

/*
 * Create an entitlement for a current user
 */
export async function createUserEntitlement ({ auth, server, body }) {
  const res = await request
    .post(`${server}/v1/entitlement`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return get(res, 'body', {})
}

/*
 * Get a users billing and subscription details
 */
export async function getUserBillingSubscriptionDetails ({
  auth,
  server,
  billingAccountId,
  billingAccountNumber,
}) {
  const res = await request
    .get(`${server}/v1/user/subscription`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber, noCache: true })
  return get(res, 'body', {})
}

/*
 * Get a users payment history
 */
export async function getUserPaymentHistory ({
  auth,
  server,
  billingAccountId,
  billingAccountNumber,
}) {
  const res = await request
    .get(`${server}/v1/payments`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
  return get(res, 'body', {})
}

/*
 * Create a user refund
 */
export async function createUserRefund ({
  auth,
  server,
  paymentId,
  amount,
  reason,
  billingAccountId,
  billingAccountNumber,
}) {
  const refundData = {
    paymentId,
    reason,
  }

  if (amount) {
    set(refundData, 'amount', parseFloat(amount).toFixed(2))
  }

  const res = await request
    .post(`${server}/v1/payments/refund`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
    .send(refundData)
  return get(res, 'body', {})
}

/*
 * Create a user refund and cancel their subscription
 */
export async function createUserRefundCancel ({
  auth,
  server,
  paymentId,
  amount,
  reason,
  billingAccountId,
  billingAccountNumber,
}) {
  const cancelData = {
    paymentId,
    reason,
  }

  if (amount) {
    set(cancelData, 'amount', parseFloat(amount).toFixed(2))
  }

  const res = await request
    .put(`${server}/v1/user/refund-and-cancel`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
    .send(cancelData)
  return get(res, 'body', {})
}

/*
 * Create a user comp
 */
export async function createUserComp ({
  auth,
  server,
  reason,
  duration,
  billingAccountId,
  billingAccountNumber,
}) {
  const compData = {
    reason,
    duration,
  }

  const res = await request
    .post(`${server}/v1/user/subscription/comp`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
    .send(compData)
  return get(res, 'body', {})
}

/*
 * Cancel a user subscription
 */
export async function createUserCancelSubscription ({
  auth,
  server,
  billingAccountId,
  billingAccountNumber,
}) {
  const res = await request
    .put(`${server}/v1/user/subscription`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
  return get(res, 'body', {})
}

/*
 * Cancel a user subscription
 */
export async function createUserReactivateSubscription (options) {
  const { auth, server, billingAccountId, billingAccountNumber, nextRatePlanId } = options
  const res = await request
    .put(`${server}/v1/user/plan`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
    .send({ nextRatePlanId })
  return get(res, 'body', {})
}
/*
 * Remove a user payment methods
 */
export async function createUserRemovePaymentMethods ({
  auth,
  server,
  billingAccountId,
  billingAccountNumber,
}) {
  const response = await request
    .post(`${server}/v1/billing/removePaymentMethods`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
  return get(response, 'body', { success: false })
}

/*
 * Get Plans
 */
export async function getPlans ({ server, language, skus }) {
  const res = await request
    .get(`${server}/v1/billing/plans`)
    .set('accept', 'application/json')
    .query({ language, skus })
  return get(res, 'body', {})
}

/*
 * Change Plan
 */
export async function changePlan ({
  nextRatePlanId,
  planSku,
  server,
  auth,
  billingAccountId,
  billingAccountNumber,
}) {
  const changePlanData = {
    nextRatePlanId,
    planSku,
  }

  const res = await request
    .put(`${server}/v1/user/plan`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
    .send(changePlanData)
  return get(res, 'body', {})
}

/*
 * Update payment method
 */
export async function updatePaymentMethod ({
  paymentMethodId,
  country,
  postalCode,
  server,
  auth,
  billingAccountId,
  billingAccountNumber,
}) {
  const updatePaymentMethodData = {
    creditCard: {
      paymentMethodId,
    },
    country,
  }

  if (postalCode) {
    set(updatePaymentMethodData, 'postalCode', postalCode)
  }

  const res = await request
    .put(`${server}/v1/billing/account/payment-method`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
    .send(updatePaymentMethodData)
  return get(res, 'body', {})
}

/*
 * Refund and Downgrade
 */
export async function createRefundDowngrade ({
  paymentId,
  nextRatePlanId,
  reason,
  prorate,
  server,
  auth,
  billingAccountId,
  billingAccountNumber,
}) {
  const refundDowngradeData = {
    paymentId,
    nextRatePlanId,
    reason,
    prorate,
  }

  const res = await request
    .put(`${server}/v1/user/refund-and-downgrade`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
    .send(refundDowngradeData)
  return get(res, 'body', {})
}

/*
 * Request currency change
 */
export async function createUserCurrencyChange ({
  auth,
  server,
  billingAccountId,
  billingAccountNumber,
  newCurrency,
  latamOverride,
}) {
  const res = await request
    .put(`${server}/v1/user/currency`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber })
    .send({ newCurrency, latamOverride })
  return get(res, 'body', { success: false })
}

/*
 * Check currency change request status
 */
export async function checkUserCurrencyChangeStatus ({ auth, server, requestId }) {
  const res = await request
    .get(`${server}/v1/user/currency-change-request/${requestId}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return get(res, 'body', {})
}

export async function anonymizeUserData (options) {
  const { config, auth, uid, tag } = options
  const server = config.getIn(['data', 'servers', 'brooklyn'])
  const response = await request
    .post(`${server}/v1/adjustments/anonymize`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send({ uid, tag })
  return get(response, 'body', { success: false })
}

export async function grantUserBonusData (options) {
  const { config, auth, email, primaryLanguage, planSku, grantDate } = options
  const server = config.getIn(['data', 'servers', 'brooklyn'])
  const response = await request
    .post(`${server}/v1/grant-plan-bonus`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send({ email, primaryLanguage, planSku, grantDate })
  return get(response, 'body', { success: false })
}

/**
 * Given the user state return true if the user is a permanent block
 * @param {Map} user The user state
 */
export function getUserStatusBlockedPermanent (user) {
  if (!Map.isMap(user)) {
    throw new Error('The user argument is required to be a Map')
  }
  return user.get('status') === USER_STATUS_BLOCKED && isNull(user.get('blocked_until'))
}

/**
 * Given the user state return true if the user is a permanent block
 * @param {Map} user The user state
 */
export function getUserStatusBlockedTemporary (user) {
  if (!Map.isMap(user)) {
    throw new Error('The user argument is required to be a Map')
  }
  return user.get('status') === USER_STATUS_BLOCKED && !isNull(user.get('blocked_until'))
}

/**
 * Get user taxes & invoice history
 */
export async function getUserInvoiceHistory ({
  auth,
  server,
  billingAccountId,
  billingAccountNumber,
}) {
  const res = await request
    .get(`${server}/v1/billing/account/invoice-history`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({ accountId: billingAccountId, accountNumber: billingAccountNumber, paidOnly: true })
  return get(res, 'body', {})
}

/**
 * Get user invoice
 */
export async function getUserInvoice ({
  auth,
  server,
  billingAccountId,
  billingAccountNumber,
  invoiceId,
}) {
  const res = await request
    .get(`${server}/v1/billing/account/invoices`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .query({
      providerName: 'zuora',
      accountId: billingAccountId,
      accountNumber: billingAccountNumber,
      invoiceId,
    })
  return get(res, 'body', {})
}

/**
 * Adjusts the price of a user's subscription
 *
 * Makes an API call to update the subscription price for a specific user.
 * This function is typically called from the AdjustPrice React component.
 *
 * @async
 * @param {Object} params - The parameters for price adjustment
 * @param {Object} params.auth - Authentication object containing user credentials
 * @param {string} params.server - The server URL to make the API request to
 * @param {number} params.price - The new price to set for the subscription
 * @param {string} params.subscriptionNumber - The Zuora subscription number
 * @param {string} params.userInfoUuid - The unique identifier of the user
 *
 * @returns {Promise<Object>} Response object containing:
 *   - success: boolean indicating if the operation was successful
 *   - Additional response data from the server
 */
export async function createUserAdjustPrice ({
  auth,
  server,
  price,
  userSubscriptionNumber: subscriptionNumber,
  userInfoUuid,
}) {
  const response = await request
    .post(`${server}/v1/billing/subscriptions/adjust-price`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send({ price, subscriptionNumber, userInfoUuid })
  return get(response, 'body', { success: false })
}

/*
 * Change account owner
 */
export async function changeAccountOwner ({
  auth,
  server,
  accountId,
  newOwnerUid,
  sendPasswordResetEmail,
  moveZuoraSubscription,
  moveEntitlements,
}) {
  const requestBody = {
    uid: newOwnerUid,
    sendPasswordResetEmail,
    moveZuoraSubscription,
    moveEntitlements,
  }

  const res = await request
    .put(`${server}/v1/account/change-owner/${accountId}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(requestBody)
  return get(res, 'body', {})
}
